import { useCallback, useEffect, useState } from 'react';
import { api, type Company, type HealthAnnual } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { money } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';
import type { Panel } from './Shell';

/**
 * Roczne rozliczenie składki zdrowotnej (E9-S3).
 *
 * Stoi obok rozliczenia rocznego PIT, bo to ta sama kartka z dwóch stron: podstawą jest **ten sam
 * dochód roczny**, tylko stawka i minimum są inne. Na wierzchu stoi dopłata albo zwrot, bo to ona
 * zaskakuje w maju — składki płacone od dochodu miesiąca poprzedniego prawie nigdy nie sumują się
 * do składki należnej za rok.
 *
 * Pokazujemy podstawę i stawkę obok kwoty, bo przy składce minimalnej zmieniają się obie: podstawą
 * jest minimalne wynagrodzenie, a stawką 9% — także u liniowca, który przez cały rok płacił 4,9%.
 * Bez tych dwóch liczb kwota wyglądałaby na pomyłkę.
 */
export function HealthAnnualPanel({
  company,
  year,
  t,
  locale,
  onGo,
}: {
  company: Company;
  year: number;
  t: Dictionary;
  locale: Locale;
  onGo?: (panel: Panel) => void;
}) {
  const [rows, setRows] = useState<HealthAnnual[] | null>(null);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api.healthAnnual(company.id, year));
    } catch (failure) {
      setRows(null);
      setError(failureOf(failure));
    }
  }, [company.id, year]);

  useEffect(() => {
    void load();
  }, [load]);

  // Firma płacąca CIT i firma bez ubezpieczenia nie mają czego rozliczać — panel wtedy nie istnieje
  if (rows !== null && rows.length === 0) return null;

  return (
    <div className="panel">
      <header>
        <h4>
          {t.healthAnnual.title} {year}
        </h4>
        <div className="sp">
          <span className="sm muted">{t.healthAnnual.deadline(year + 1)}</span>
        </div>
      </header>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      {rows?.map((row) => (
        <div key={row.partnerId ?? 'own'} className="panel">
          <header>
            <h5>
              {t.healthAnnual.forms[row.taxationForm]}
              {row.taxpayer && ` · ${row.taxpayer}`}
            </h5>
            {/* Dodatnia różnica to dopłata, ujemna — nadpłata do zwrotu z ZUS-u */}
            <div className="sp">
              <span className={Number(row.difference) > 0 ? 'chip st-warn' : 'chip st-ok'}>
                <i />
                {Number(row.difference) >= 0 ? t.healthAnnual.toPay : t.healthAnnual.toRefund}{' '}
                {money(Math.abs(Number(row.difference)), locale)}
              </span>
            </div>
          </header>
          <dl className="brk">
            {row.taxationForm === 'RYCZALT' ? (
              <>
                <dt>{t.healthAnnual.revenue}</dt>
                <dd>{money(row.revenue, locale)}</dd>
              </>
            ) : (
              <>
                <dt>{t.healthAnnual.income}</dt>
                <dd>{money(row.income, locale)}</dd>
              </>
            )}
            <dt>{t.healthAnnual.months}</dt>
            <dd>{row.months}</dd>
            <dt>{t.healthAnnual.basis}</dt>
            <dd>{money(row.basis, locale)}</dd>
            <dt>{t.healthAnnual.rate}</dt>
            {/* Stawkę pokazujemy w procentach, bo tak ją zna każdy: 9% i 4,9%, nie 0,09 i 0,049 */}
            <dd>{`${(Number(row.rate) * 100).toFixed(1).replace(/\.0$/, '').replace('.', ',')}%`}</dd>
            <dt>{t.healthAnnual.due}</dt>
            <dd>{money(row.due, locale)}</dd>
            <dt>{t.healthAnnual.paid}</dt>
            <dd>{money(row.paid, locale)}</dd>
            <dt className="tot">
              {Number(row.difference) >= 0 ? t.healthAnnual.toPay : t.healthAnnual.toRefund}
            </dt>
            <dd className="tot">{money(Math.abs(Number(row.difference)), locale)}</dd>
          </dl>
        </div>
      ))}

      {/* Czego rachunek nie obejmuje — zdaniami z serwera, nie skrótem */}
      {rows?.[0]?.notes.map((note) => (
        <p className="note" key={note}>
          <Icon.Info className="inline-icon" /> {t.healthAnnual.notes[note]}
        </p>
      ))}
    </div>
  );
}
